import * as MuiColors from "@mui/material/colors";
import type { HexColor } from "common/utilities/colors/colors.types";
import type { SequenceFeatureTypeColorSetting } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { FeatureId } from "sequenceAnalyser/utilities/sequenceAnalyser.types";

export type FeatureTypesColorSetting = Record<string, SequenceFeatureTypeColorSetting>;

/** All other feature types default to 'I' (Identical) */
export const defaultFeatureTypesColorSetting: FeatureTypesColorSetting = {
  CDR: "D",
  Framework: "D",
  misc_marker: "S",
  misc_difference: "S",
  PTM: "S",
  cloning_site: "S",
};

export type FeatureTypesHexColor = Record<string, HexColor>;

const notImportantColor = MuiColors.grey[400];

/**
 * Default feature types colors.
 * - comments after feature types are feedback from scientist
 * - not important features go grey notImportantColor
 * - re-using colors is important as they are very limited:
 *   - colors can be re-used with similar features types
 *   - colors can be re-used with features types that rarely appear together
 */
export const defaultFeatureTypesColor: FeatureTypesHexColor = {
  // List from GEN_NU_FEATURE_TYPE table
  "Mature Chain": MuiColors.blue["600"], // #1e88e5
  CDR: MuiColors.lightBlue[300], // from defaultFeatureNamesColor
  Framework: MuiColors.lime[600], // from defaultFeatureNamesColor
  V_segment: notImportantColor,
  polyA_signal: MuiColors.orange[500], // #ff9800
  CDS: MuiColors.red[500], // #f44336
  promoter: MuiColors.brown["600"], // #6d4c41
  misc_feature: MuiColors.red[500], // #f44336
  misc_marker: MuiColors.blueGrey[400], // #78909c same as misc_difference
  primer: MuiColors.teal[600], // #00897b
  primer_bind: MuiColors.teal[600], // #00897b
  rep_origin: notImportantColor,
  sig_peptide: MuiColors.orange[500], // #ff9800
  misc_difference: MuiColors.blueGrey[400], // #78909c
  "Framework Mutation": MuiColors.red.A400, // #ff1744
  "Insertion Point": MuiColors.pink[600], // #d81b60
  Replacement: MuiColors.cyan[600], // #00acc1
  "Stop Codon": MuiColors.red.A400, // #ff1744 different vibrant color?
  "VL-end recognition sequence": notImportantColor,
  "VH-end recognition sequence": notImportantColor,
  V_region: notImportantColor,
  "Restriction Site": MuiColors.brown[400], // #8d6e63
  Stuffer: MuiColors.pink[600], // #d81b60
  C_region: MuiColors.brown[300], // #a1887f start after framework
  PTM: MuiColors.pink[400], // #ec407a
  "V-Region End": notImportantColor,
  exon: MuiColors.cyan[400], // #26c6da
  mutation: MuiColors.red.A400, // #ff1744
  Leader: MuiColors.orange[500], // #ff9800
  Tag: MuiColors.cyan.A200, // #18ffff vibrant blue
  POI: MuiColors.pink[600], // #d81b60
  cloning_site: notImportantColor,
  "Parent Mutation": MuiColors.red.A200, // #ff5252 similar to framework mutation
  "Design Target Mutation": MuiColors.green[900], // #1b5e20
  DVD: MuiColors.blueGrey[400], // #78909c barely used, same color as misc_difference
  // eslint-disable-next-line @stylistic/quotes
  "5'UTR": MuiColors.orange[500], // #ff9800
  Loop: MuiColors.orange[500], // #ff9800
  "Design | Conserved position": "#7cb342",
  "Design | Expected mutation": "#8e24aa",
  "Design | Not designed mutation": "#fdd835",
  "Design | Unexpected conservation": "#00acc1",
  "Design | Unexpected mutation": MuiColors.amber[500], // #ffc107 can overlap PTM
  "Design | Insertions or deletions": "#42a5f5",

  // GEN_AA_FEATURE_TYPE only
  Site: MuiColors.green[600], // #43a047
  misc_feat: MuiColors.red[500], // #f44336
  "Other Ab Region": MuiColors.cyan[400], // #26c6da
  "Signal Peptide": MuiColors.orange[500], // #ff9800 same as sig_peptide
  Linker: MuiColors.pink.A400, // #f50057 similar to framework mutation
  mat_peptide: MuiColors.blue[600], // #1e88e5
  Region: MuiColors.blueGrey[700], // #455a64
  "Sequence Border": MuiColors.teal[200], // #80cbc4
  proprotein: MuiColors.deepPurple[200], // #b39ddb same as precursor
  source: MuiColors.blue[200], // #90caf9 light blue
  Protein: MuiColors.blue[600], // #1e88e5 same as mature peptide
  Precursor: MuiColors.deepPurple[200], // #b39ddb
  mature_peptide: MuiColors.blue[600], // #1e88e5
  "Hot Spot": MuiColors.red[500], // #f44336
  "DNA read match": MuiColors.yellow[600], // #fdd835
  Peptide: MuiColors.blue[600], // #1e88e5 same as mature peptide
  Intein: MuiColors.cyan[400], // #26c6da
};

export type FeatureNameHexColor = Record<FeatureId, HexColor>;

/**
 * Default feature names color used when not provided by the API
 */
export const defaultFeatureNamesColor: FeatureNameHexColor = {
  CDR_LCDR1: MuiColors.indigo[100],
  CDR_LCDR2: MuiColors.indigo[300],
  CDR_LCDR3: MuiColors.indigo[400],
  CDR_HCDR1: MuiColors.lightBlue[100],
  CDR_HCDR2: MuiColors.lightBlue[200],
  CDR_HCDR3: MuiColors.lightBlue[300],
  Framework_HFW1: MuiColors.lime[100],
  Framework_HFW2: MuiColors.lime[300],
  Framework_HFW3: MuiColors.lime[500],
  Framework_HFW4: MuiColors.lime[600],
  Framework_LFW1: MuiColors.green[100],
  Framework_LFW2: MuiColors.green[200],
  Framework_LFW3: MuiColors.green[300],
  Framework_LFW4: MuiColors.green[500],
};

export const featureTypeFeatureNames = {
  CDR: ["LCDR1", "LCDR2", "LCDR3", "HCDR1", "HCDR2", "HCDR3"],
  Framework: ["HFW1", "HFW2", "HFW3", "HFW4", "LFW1", "LFW2", "LFW3", "LFW4"],
};

export interface Defaults {
  defaultFeatureTypesColorSetting: FeatureTypesColorSetting;
  defaultFeatureTypesColor: FeatureTypesHexColor;
  defaultFeatureNamesColor: FeatureNameHexColor;
}
